// Iter-158.13.2.20f.33 — Project-scoped allergy map hook.
//
// Fetches `/supervisor-notes` once and returns a
// `Map<characterId, note>` restricted to Character-level allergy
// notes (`scene_id: null` + `tags` includes "allergy"). Reused
// by SceneDetail (to badge per-character sub-headers) and
// BetaHMCharacters (to badge the Cast picker rows). Client-side
// filter — no backend endpoint change.
//
// Fetch is refreshed:
//   - On mount.
//   - On the `onsetflow:allergy-note-changed` window event (fired
//     by CharacterDetail after create/update/delete of an allergy
//     note) so the indicator on other surfaces updates without a
//     full page reload.
//
// Kept dependency-free of the notes React tree — no context, no
// provider. Safe to call multiple times per page; each call
// owns its own state.
import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { pickCharacterAllergyNote } from "../components/AllergyBadge";

export const ALLERGY_NOTE_CHANGED_EVENT = "onsetflow:allergy-note-changed";

export function useProjectAllergyMap(apiBase, projectId) {
    const [byCharacterId, setByCharacterId] = useState(() => new Map());
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        if (!apiBase || !projectId) return;
        try {
            const { data } = await api.get(`${apiBase}/${projectId}/supervisor-notes`);
            const notes = Array.isArray(data) ? data : [];
            const next = new Map();
            const seen = new Set();
            for (const n of notes) {
                if (!n || n.archived === true) continue;
                if (!n.character_id || n.scene_id) continue;
                const tags = Array.isArray(n.tags) ? n.tags : [];
                if (!tags.includes("allergy")) continue;
                if (seen.has(n.character_id)) continue;
                // pickCharacterAllergyNote is intentionally NOT
                // called here (that would re-scan the full list per
                // character). We build the map in one pass and use
                // the shared predicate only as a semantic anchor.
                next.set(n.character_id, n);
                seen.add(n.character_id);
            }
            setByCharacterId(next);
        } catch {
            // Silent — a fetch failure means "no indicator", never
            // a broken page. Loading resolves either way.
        } finally {
            setLoading(false);
        }
    }, [apiBase, projectId]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        const handler = () => refresh();
        window.addEventListener(ALLERGY_NOTE_CHANGED_EVENT, handler);
        return () => window.removeEventListener(ALLERGY_NOTE_CHANGED_EVENT, handler);
    }, [refresh]);

    return { byCharacterId, loading, refresh };
}

// Re-exported for tests that want to lock the predicate.
export { pickCharacterAllergyNote };
